import { useCallback, useEffect, useRef, useState } from "react";

import { resolveTextTrack } from "../../components/player-ui/helpers";
import * as Constants from "../../constants";
import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { useMediaContext } from "../../contexts/MediaContext";
import { usePlayerStore } from "../../store/usePlayerStore";
import { Subtitles } from "../../types/player-api";

import { resolveSelectedSubtitles } from "./textTrackHelpers";

const useTextTracks = ({ video }: { video?: HTMLVideoElement }): void => {
  const { useStoreState } = usePlayerStore();

  const device = useStoreState.device();
  const isFullscreen = useStoreState.isFullscreen();
  const isNativeSubtitles = useStoreState.isNativeSubtitles();
  const isPictureInPicture = useStoreState.isPictureInPicture();
  const selectedSubtitles = useStoreState.selectedSubtitles();
  const subtitles = useStoreState.subtitles();
  const userPreferredSubtitles = useStoreState.userPreferredSubtitles();

  const setIsNativeSubtitles = useStoreState.setIsNativeSubtitles();
  const setSelectedSubtitles = useStoreState.setSelectedSubtitles();
  const setSubtitles = useStoreState.setSubtitles();
  const setSubtitlesCue = useStoreState.setSubtitlesCue();
  const setSubtitlesCues = useStoreState.setSubtitlesCues();
  const useVTTPositioning = useStoreState.useVTTPositioning();

  const {
    language,
    subtitles: subtitlesEnabled,
    subtitlesDefault
  } = useEmbedParametersContext();
  const { serviceSchedule } = useMediaContext();

  const [nativeHlsSupport, setNativeHlsSupport] = useState<boolean>(false);

  const selectedSubtitlesRef = useRef<Subtitles>(undefined);
  selectedSubtitlesRef.current = selectedSubtitles;

  const usingServiceScheduleTracks = !!serviceSchedule?.length;

  useEffect(() => {
    setSelectedSubtitles(
      resolveSelectedSubtitles(
        language,
        subtitles,
        subtitlesEnabled,
        subtitlesDefault,
        userPreferredSubtitles
      )
    );
  }, [
    language,
    setSelectedSubtitles,
    subtitles,
    subtitlesDefault,
    subtitlesEnabled,
    userPreferredSubtitles
  ]);

  useEffect(() => {
    if (selectedSubtitles && selectedSubtitles.kind === "off") {
      setSubtitlesCue("");
    }
  }, [selectedSubtitles, setSubtitlesCue]);

  useEffect(() => {
    if (video) {
      setNativeHlsSupport(!!video.canPlayType(Constants.HLS_MIME_TYPE));
    }
  }, [video]);

  useEffect(() => {
    setIsNativeSubtitles(
      (isFullscreen && device.os.name === "iOS") ||
        (isPictureInPicture && nativeHlsSupport)
    );
  }, [
    device,
    isFullscreen,
    isPictureInPicture,
    nativeHlsSupport,
    setIsNativeSubtitles
  ]);

  const resolveTrackMode = useCallback(
    (trackLabel?: string) =>
      subtitlesEnabled &&
      selectedSubtitles?.label === trackLabel?.toLocaleLowerCase()
        ? isNativeSubtitles
          ? "showing"
          : "hidden"
        : "disabled",
    [isNativeSubtitles, selectedSubtitles, subtitlesEnabled]
  );

  useEffect(() => {
    if (video && selectedSubtitles && subtitlesEnabled) {
      for (const track of Array.from(video.textTracks as TextTrackList)) {
        track.mode = resolveTrackMode(track.label);
      }
    }
  }, [
    selectedSubtitles,
    subtitlesEnabled,
    video,
    isNativeSubtitles,
    resolveTrackMode
  ]);

  const onTrackAdd = useCallback(
    ({ track }: TrackEvent) => {
      if (track) {
        track.mode = resolveTrackMode(track.label);
        resolveTextTrack({
          setSubtitles,
          setSubtitlesCue,
          setSubtitlesCues,
          textTrack: track,
          useVTTPositioning,
          usingServiceScheduleTracks
        });
      }
    },
    [
      resolveTrackMode,
      setSubtitles,
      setSubtitlesCue,
      setSubtitlesCues,
      useVTTPositioning,
      usingServiceScheduleTracks
    ]
  );

  const onTrackChange = useCallback(() => {
    if (video) {
      for (const track of Array.from(video.textTracks as TextTrackList)) {
        track.mode = resolveTrackMode(track.label);
      }
    }
  }, [resolveTrackMode, video]);

  useEffect(() => {
    if (video?.textTracks) {
      video.textTracks.addEventListener?.("addtrack", onTrackAdd);
      video.textTracks.addEventListener?.("change", onTrackChange);
      return () => {
        video.textTracks.removeEventListener?.("addtrack", onTrackAdd);
        video.textTracks.removeEventListener?.("change", onTrackChange);
      };
    }
  }, [onTrackAdd, onTrackChange, video]);
};

export default useTextTracks;
